/* eslint-disable react-hooks/exhaustive-deps */
import constate from 'constate'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useWorkspaceContext } from './useWorkspace'
import apiClient from '../api/apiClient'

export enum ComponentType {
  autocomplete = 'autocomplete',
  checkboxImage = 'checkboxImage',
  radio = 'radio',
}

interface HistoryResponse {
  status: string
  code: number
  context: any
  questions: HistoryQuestion[]
  data: any[]
}

export interface HistoryQuestion {
  filled: boolean
  id: number
  label: string
  answerType: string
  options: Option[] | null
  questionPrototype?: Prototype
  responsePrototype?: Prototype
}

interface Option {
  text: string
  value: string
  key?: string[]
}

interface Prototype {
  key: string[]
  template: string
}

interface Requirement {
  id: number
  answer: string[]
}

export interface IQuestionnaire {
  questions: IQuestion[]
  answers: {
    id: number
    answer: string | string[]
  }[]
  totalAnswers: number
  totalQuestions: number
  summary: string
}

// For Image resource object
interface ImageResource {
  '2x': string
  '2x-min': string
  '4x': string
  '4x-min': string
  svg: string
}

export interface Resource {
  value: string
  image?: ImageResource
}

export interface IQuestion {
  question_id: number
  question: string
  label: string
  hint: string | null
  placeholder?: string | null
  answer_type: 'string' | 'array.string'
  options: string[]
  component_type: ComponentType
  requirements?: Requirement[]
  resources?: Resource[] | null
  options_detail?: { option_id: string; name: string; option_name: string; description: string }[]
}

export const useUiAdviser = () => {
  const { selectedNodeId } = useWorkspaceContext()
  const { projectId } = useParams()
  const [quickChat, setQuickChat] = useState('')
  const [isQuestionnaireLoading, setIsQuestionaryLoading] = useState(true)

  const [questionnaire, setQuestionnaire] = useState<IQuestionnaire>()
  const [questionnaireQuestions, setQuestionnaireQuestions] = useState<IQuestion[]>()

  // .......
  const fetchQuestionnaireOnly = async (standardId?: string) => {
    const { data } = await apiClient.get(`/workspace/${selectedNodeId}/project/${projectId}/ai-adviser/questionnaire`, {
      params: { standardId: standardId || '' },
    })

    setQuestionnaireQuestions(data.questions)
  }
  const fetchChatQuestionnaire = async (standardId?: string) => {
    try {
      const { data } = await apiClient.get(
        `/workspace/${selectedNodeId}/project/${projectId}/ai-adviser/questionnaire`,
        {
          params: { standardId: standardId || '' },
        },
      )
      setQuestionnaireQuestions(data.questions)

      setQuestionnaire(data)
      setIsQuestionaryLoading(false)
    } catch (e) {
      setIsQuestionaryLoading(false)
    }
  }

  const fetchChatHistory = async () => {
    try {
      const { data } = await apiClient.get<HistoryResponse>(
        `/workspace/${selectedNodeId}/project/${projectId}/ai-adviser/prompt-history`,
      )

      const chatHistory = data.data
        .filter((r) => r.message)
        .map((r) => [
          { sender: 'USER', text: r.message },
          { sender: 'API', text: r.prompt, id: r._id, feedback: r.feedback, score: r.score },
        ])
        .flat()

      return { chatHistory, context: data.context, questions: data.questions }
    } catch (e) {
      return null
    }
  }

  const fetchProjectTags = async () => {
    try {
      const { data } = await apiClient.get(`/workspace/${selectedNodeId}/project/${projectId}/ai-adviser/tags`)

      return data
    } catch (e) {
      console.log(e)
    }
  }

  const postChatQuestionnaireAnswers = async (
    answers: { id: number; answer: any }[],
    selectedCertification?: string,
  ) => {
    const { data } = await apiClient.post(
      `/workspace/${selectedNodeId}/project/${projectId}/ai-adviser/questionnaire`,
      {
        answers,
      },
    )
    await fetchChatQuestionnaire(selectedCertification || '')
    return data.data
  }

  const postChatMessageStream = async (channel: string, text: string) => {
    const { data } = await apiClient.post(
      `/workspace/${selectedNodeId}/project/${projectId}/ai-adviser/prompt-python-stream`,
      {
        channel,
        message: text,
      },
    )

    return data.data
  }

  const postAiChatMessageFeedback = async (feedback: string, userComment: string, promptId: string) => {
    const { data } = await apiClient.post(
      `/workspace/${selectedNodeId}/project/${projectId}/ai-adviser/prompt/${promptId}/feedback`,
      {
        feedback,
        userComment,
      },
    )

    return data.data
  }

  useEffect(() => {
    if (projectId && selectedNodeId) {
      fetchChatQuestionnaire()
    }
  }, [projectId, selectedNodeId])

  return {
    fetchChatQuestionnaire,
    postChatQuestionnaireAnswers,
    postChatMessageStream,
    fetchChatHistory,
    setQuickChat,
    quickChat,
    questionnaire,
    postAiChatMessageFeedback,
    fetchProjectTags,
    isQuestionnaireLoading,
    fetchQuestionnaireOnly,
    questionnaireQuestions,
  }
}

const [UiAdviserContainer, userUiAdviserContext] = constate(useUiAdviser)

export { UiAdviserContainer, userUiAdviserContext }
